import { useEffect, useState } from 'react';
import { CreateEditFormDrawer } from 'framework/ViewTypes/contentLayout';
import type { FormFieldsData } from 'framework/form';
import type { SourcingFilterKey, SourcingFilterOptions, SourcingFilters } from '../../../../types';
import { isTechnologyRequired } from 'domain/shared/career';
import { SOURCING_FILTERS } from '../../constants';
import { cloneFilters } from '../../utils/utils';
import { findSourcingSpecialization, isSourcingSelectionComplete } from '../../utils/sourcingFilterModel';
import { getSaveSourcingFilterValidationSchemaFunc } from './saveSourcingFilterDrawerValidation';
import getSaveSourcingFilterDrawerSections from './SaveSourcingFilterDrawerSections';
import { useMessageService, SuccessMessage } from 'framework/ui/Message';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  parameters: SourcingFilters;
  filterOptions: SourcingFilterOptions;
  onSave: (name: string, filters: SourcingFilters) => Promise<void>;
};

const removeFilter = (filters: SourcingFilters, key: SourcingFilterKey): SourcingFilters => {
  if (key === SOURCING_FILTERS.specialization) return filters;
  const next = cloneFilters(filters);
  delete next[key];
  next.stricts = (next?.stricts ?? []).filter(strictKey => strictKey !== key);
  return next;
};

const SaveSourcingFilterDrawer = ({ isOpen, onClose, parameters, filterOptions, onSave }: Props) => {
  const { addMessage } = useMessageService();
  const [selectedParameters, setSelectedParameters] = useState(() => cloneFilters(parameters));
  const [criteriaError, setCriteriaError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCriteriaError('');
      setSelectedParameters(cloneFilters(parameters));
    }
  }, [isOpen, parameters]);

  const specialization = findSourcingSpecialization(filterOptions, selectedParameters.specializationId);
  const isTechnologyRequiredValue = isTechnologyRequired(specialization?.specializationId);

  return (
    <CreateEditFormDrawer
      key={isOpen ? 'save-open' : 'save-closed'}
      isOpen={isOpen}
      onClose={onClose}
      title="Save sourcing filter"
      header="Save the current sourcing criteria to quickly apply them again later"
      initialData={{ filterName: '' }}
      isCreateRequest
      handleDirty={false}
      submitButtonTitle="Save filter"
      afterSubmit={{
        execute: () => {
          addMessage(new SuccessMessage('Sourcing filter saved successfully.'));
        },
      }}
      getValidationSchemaFunc={getSaveSourcingFilterValidationSchemaFunc}
      submitFormFunc={async (data: FormFieldsData) => {
        if (!isSourcingSelectionComplete(filterOptions, selectedParameters)) {
          const message =
            specialization && isTechnologyRequiredValue
              ? 'Technology is required for the selected specialization.'
              : 'Specialization is required.';
          setCriteriaError(message);
          throw message;
        }
        await onSave(data.filterName, selectedParameters);
        onClose();
        return {};
      }}
      getSections={() =>
        getSaveSourcingFilterDrawerSections({
          selectedParameters,
          filterOptions,
          criteriaError,
          onRemoveFilter: key => setSelectedParameters(current => removeFilter(current, key)),
        })
      }
      width={680}
    />
  );
};

export default SaveSourcingFilterDrawer;
